import React, { useState, useEffect, useRef } from 'react';
import './footer.css';

import app_store from '../img/app-store.svg';
import google_play from '../img/google-play.svg';
import logo_footer from '../img/logo-footer.svg';

const Footer = ({ fullWidth = false }) => {
  const footerRef = useRef(null);
  const startY = useRef(0);
  const isDragging = useRef(false);

  const ContainerWrapper = ({ children }) => {
    if (fullWidth) {
      return <div className="container-fluid footer-wide-container">{children}</div>;
    }
    return <div className="container">{children}</div>;
  };

  useEffect(() => {
    const checkIfAtBottom = () => {
      const scrollHeight = document.documentElement.scrollHeight;
      const windowHeight = window.innerHeight;
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      // Проверяем, что страница прокручена до низа
      return scrollHeight - (scrollTop + windowHeight) <= 30;
    };

    const handleTouchStart = (e) => {
      // Проверяем что мы внизу страницы
      if (!checkIfAtBottom()) return;
      
      // Запоминаем начальную позицию
      startY.current = e.touches[0].clientY;
      isDragging.current = true;
      
      // Добавляем класс для стилизации
      if (footerRef.current) {
        footerRef.current.classList.add('footer-pulling');
      }
    };

    const handleTouchMove = (e) => {
      if (!isDragging.current || !footerRef.current) return;
      
      const currentY = e.touches[0].clientY;
      const diff = currentY - startY.current;
      
      // Только если тянем вверх (diff отрицательный)
      if (diff < 0) {
        e.preventDefault();
        // Ограничиваем максимальное расстояние 100px
        const pullDistance = Math.min(Math.abs(diff), 100);
        footerRef.current.style.transform = `translateY(-${pullDistance}px)`;
      } else if (diff > 0 && footerRef.current.style.transform !== 'translateY(0px)') {
        // Если тянем вниз - возвращаем
        footerRef.current.style.transform = 'translateY(0px)';
      }
    };

    const handleTouchEnd = () => {
      if (!footerRef.current) return;
      
      if (isDragging.current) {
        // Плавно возвращаем футер на место
        footerRef.current.style.transition = 'transform 0.3s cubic-bezier(0.2, 0.9, 0.4, 1.1)';
        footerRef.current.style.transform = 'translateY(0px)';
        
        // Убираем класс
        footerRef.current.classList.remove('footer-pulling');
        
        // Сбрасываем transition после анимации
        setTimeout(() => {
          if (footerRef.current) {
            footerRef.current.style.transition = '';
          }
        }, 300);
      }
      
      isDragging.current = false;
    };

    // Используем mouse события для эмуляции на десктопе
    const handleMouseDown = (e) => {
      if (!checkIfAtBottom()) return;
      
      startY.current = e.clientY;
      isDragging.current = true;
      
      if (footerRef.current) {
        footerRef.current.classList.add('footer-pulling');
      }
      
      // Блокируем выделение текста
      e.preventDefault();
    };

    const handleMouseMove = (e) => {
      if (!isDragging.current || !footerRef.current) return;
      
      const currentY = e.clientY;
      const diff = currentY - startY.current;
      
      if (diff < 0) {
        const pullDistance = Math.min(Math.abs(diff), 100);
        footerRef.current.style.transform = `translateY(-${pullDistance}px)`;
      } else if (diff > 0 && footerRef.current.style.transform !== 'translateY(0px)') {
        footerRef.current.style.transform = 'translateY(0px)';
      }
    };

    const handleMouseUp = () => {
      if (!footerRef.current) return;
      
      if (isDragging.current) {
        footerRef.current.style.transition = 'transform 0.3s cubic-bezier(0.2, 0.9, 0.4, 1.1)';
        footerRef.current.style.transform = 'translateY(0px)';
        footerRef.current.classList.remove('footer-pulling');
        
        setTimeout(() => {
          if (footerRef.current) {
            footerRef.current.style.transition = '';
          }
        }, 300);
      }
      
      isDragging.current = false;
    };

    // Добавляем обработчики для touch (мобильные) и mouse (десктоп эмуляция)
    const footerElement = footerRef.current;
    
    if (footerElement) {
      // Touch события
      footerElement.addEventListener('touchstart', handleTouchStart);
      footerElement.addEventListener('touchmove', handleTouchMove, { passive: false });
      footerElement.addEventListener('touchend', handleTouchEnd);
      
      // Mouse события для эмуляции
      footerElement.addEventListener('mousedown', handleMouseDown);
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      if (footerElement) {
        footerElement.removeEventListener('touchstart', handleTouchStart);
        footerElement.removeEventListener('touchmove', handleTouchMove);
        footerElement.removeEventListener('touchend', handleTouchEnd);
        footerElement.removeEventListener('mousedown', handleMouseDown);
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      }
    };
  }, []);

  return (
    <footer 
      ref={footerRef}
      className="footer-section"
      style={{
        position: 'relative',
        transform: 'translateY(0)',
        willChange: 'transform',
        cursor: 'grab'
      }}
    >
      {/* Индикатор потягивания */}
      <div className="pull-indicator-wrapper">
        <div className="pull-indicator"></div>
      </div>
      
      <ContainerWrapper>
        <div className="footer-top-part">
          <div className="footer-columns-wrapper">
            {/* Колонка 1: Міська платформа */}
            <div className="footer-col">
              <h6>Міська платформа</h6>
              <ul className="list-unstyled">
                <li><a href="https://portal.kyiv.digital/services">Міськи послуги</a></li>
                <li><a href="https://portal.kyiv.digital/faqs">Часті питання</a></li>
              </ul>
            </div>
            
            {/* Колонка 2: Соціальні мережі */}
            <div className="footer-col">
              <h6>Соціальні мережі</h6>
              <div className="social-icons">
                <a href="https://www.facebook.com/KyivDigital"><i className="fab fa-facebook-f"></i></a>
                <a href="https://www.instagram.com/kyiv.digital/"><i className="fab fa-instagram"></i></a>
                <a href="https://t.me/Kyiv_digital"><i className="fab fa-telegram-plane"></i></a>
                <a href="https://www.youtube.com/@kyiv.digital"><i className="fab fa-youtube" /></a>
              </div>
            </div>
            
            {/* Колонка 3: Мобільний застосунок */}
            <div className="footer-col">
              <h6>Мобільний застосунок Київ Цифровий</h6>
              <div className="app-stores">
                <a href="https://apps.apple.com/ua/app/kyiv-digital/id1534007479"><img src={app_store} alt="App Store" /></a>
                <a href="https://play.google.com/store/apps/details?id=com.kyivdigital&pcampaignid=web_share"><img src={google_play} alt="Google Play" /></a>
              </div>
            </div>
            
            {/* Колонка 4: Логотип */}
            <div className="footer-col footer-logo-col">
              <div className="footer-logo-wrapper">
                <a className="footer-brand" href="#">
                  <img src={logo_footer} alt="Logo" />
                </a>
              </div>
            </div>
          </div>
        </div>
        
        <hr className="footer-divider" />
        
        <div className="footer-bottom-part">
          <p>
            2025 © Київська міська державна адміністрація, Київська міська рада
          </p>
          <p>
            <span>E-mail: <a href="mailto:support.osvita@kyivcity.gov.ua">support.osvita@kyivcity.gov.ua</a></span>
            <span>Тел: <a href="tel:0443668015">(044) 366-80-15</a></span>
          </p>
        </div>
      </ContainerWrapper>
    </footer>
  );
};

export default Footer;